import React from 'react';

interface SkeletonLoaderProps {
  type?: 'card' | 'table' | 'list' | 'profile' | 'dashboard' | 'custom';
  rows?: number;
  className?: string;
  showAvatar?: boolean;
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  type = 'card',
  rows = 3,
  className = '',
  showAvatar = false
}) => {
  const baseClasses = 'animate-pulse bg-gray-200 rounded';

  const renderCardSkeleton = () => (
    <div className={`bg-white p-6 rounded-xl shadow-soft border border-gray-100 ${className}`}>
      <div className="space-y-4">
        {showAvatar && (
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
            <div className="space-y-2 flex-1">
              <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
            </div>
          </div>
        )}
        <div className="h-4 bg-gray-200 rounded animate-pulse w-full"></div>
        <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6"></div>
        <div className="h-4 bg-gray-200 rounded animate-pulse w-4/6"></div>
        <div className="flex space-x-2 mt-4">
          <div className="h-8 bg-gray-200 rounded animate-pulse w-20"></div>
          <div className="h-8 bg-gray-200 rounded animate-pulse w-16"></div>
        </div>
      </div>
    </div>
  );

  const renderTableSkeleton = () => (
    <div className={`bg-white rounded-xl shadow-soft border border-gray-100 overflow-hidden ${className}`}>
      {/* Table Header */}
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
        <div className="flex space-x-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-4 bg-gray-200 rounded animate-pulse flex-1"></div>
          ))}
        </div>
      </div>
      
      {/* Table Rows */}
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="px-6 py-4 border-b border-gray-100 last:border-b-0">
          <div className="flex items-center space-x-4">
            {showAvatar && (
              <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
            )}
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
            </div>
            <div className="flex space-x-2">
              <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderListSkeleton = () => (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="bg-white p-4 rounded-lg shadow-soft border border-gray-100">
          <div className="flex items-center space-x-3">
            {showAvatar && (
              <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
            )}
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
            </div>
            <div className="h-6 w-16 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderProfileSkeleton = () => (
    <div className={`bg-white p-6 rounded-xl shadow-soft border border-gray-100 ${className}`}>
      <div className="flex items-center space-x-4 mb-6">
        <div className="w-16 h-16 bg-gray-200 rounded-full animate-pulse"></div>
        <div className="space-y-2 flex-1">
          <div className="h-5 bg-gray-200 rounded animate-pulse w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3"></div>
          <div className="h-3 bg-gray-200 rounded animate-pulse w-1/4"></div>
        </div>
      </div>
      
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="space-y-2">
            <div className="h-3 bg-gray-200 rounded animate-pulse w-1/4"></div>
            <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDashboardSkeleton = () => (
    <div className={`space-y-6 ${className}`}>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="bg-white p-6 rounded-xl shadow-soft border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gray-200 rounded-xl animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded animate-pulse w-12"></div>
            </div>
            <div className="space-y-2">
              <div className="h-8 bg-gray-200 rounded animate-pulse w-16"></div>
              <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts and Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100">
          <div className="h-6 bg-gray-200 rounded animate-pulse w-1/3 mb-4"></div>
          <div className="h-48 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100">
          <div className="h-6 bg-gray-200 rounded animate-pulse w-1/3 mb-4"></div>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
                <div className="flex-1 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-12 h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Render based on type
  switch (type) {
    case 'table':
      return renderTableSkeleton();
    case 'list':
      return renderListSkeleton();
    case 'profile':
      return renderProfileSkeleton();
    case 'dashboard':
      return renderDashboardSkeleton();
    case 'card':
    default:
      return renderCardSkeleton();
  }
};

// Specialized skeleton components
export const TableSkeleton: React.FC<{ rows?: number; showAvatar?: boolean }> = ({ rows = 5, showAvatar = true }) => (
  <SkeletonLoader type="table" rows={rows} showAvatar={showAvatar} />
);

export const CardSkeleton: React.FC<{ showAvatar?: boolean }> = ({ showAvatar = false }) => (
  <SkeletonLoader type="card" showAvatar={showAvatar} />
);

export const ListSkeleton: React.FC<{ rows?: number; showAvatar?: boolean }> = ({ rows = 5, showAvatar = true }) => (
  <SkeletonLoader type="list" rows={rows} showAvatar={showAvatar} />
);

export const ProfileSkeleton: React.FC = () => (
  <SkeletonLoader type="profile" />
);

export const DashboardSkeleton: React.FC = () => (
  <SkeletonLoader type="dashboard" />
);

export default SkeletonLoader;
